import {
    useEffect,
    useRef,
    useState,
    useCallback
} from 'react';
import {
    CPIEResponse
} from '../types/newOpcodeTypes.types';

// Prefer a dedicated WS URL when set; otherwise derive from BEX_SERVER_URL
// so local dev "just works" without a separate env var.
const BASE_URL = process.env.REACT_APP_BEX_SERVER_WEBSOCKET_URL
    || (process.env.REACT_APP_BEX_SERVER_URL || '').replace(/^http/, 'ws');

export function useWebSocket(connectionId: string, url: string) {
    const [isConnected, setIsConnected] = useState(false);
    const [isExecuting, setIsExecuting] = useState(false);
    const [errorResult, setErrorResult] = useState('');
    const [messages, setMessages] = useState<CPIEResponse[]>([]);
    // A-0008.05: server-issued task_id from Mind's `begin` event, propagated by bex-server.
    const [taskId, setTaskId] = useState<string | null>(null);

    const socketRef = useRef<WebSocket | null>(null);
    const reconnectTimeout = useRef<NodeJS.Timeout | null>(null);
    const isManuallyClosed = useRef(false);

    const connect = useCallback(() => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            return;
        }

        performance.now();
        const ws = new WebSocket(`${BASE_URL}/${url}?clientId=${connectionId}`);
        socketRef.current = ws;
        isManuallyClosed.current = false;

        ws.onopen = () => {
            ws.send(JSON.stringify({
                type: 'RESUME'
            }));
            setIsConnected(true);
        };

        ws.onmessage = (event) => {
            let response: string;
            try {
                const data = JSON.parse(event.data);
                if (data?.type === 'PROCESS_STARTED') return;
                if (data?.type === 'PROCESS_DONE') return;

                // A-0008.05: server-issued task_id arrives ahead of any PROCESS_STREAM.
                if (data?.type === 'PROCESS_TASK_ID') {
                    if (typeof data.taskId === 'string') setTaskId(data.taskId);
                    return;
                }

                if (data?.type === 'RESUME_STATE') {
                    if (!data.processes || data.processes.length === 0) {
                        setIsExecuting(false);
                        return;
                    }
                    const resumeMessage = data.processes[0];
                    // A-0008.05: resume carries taskId so the indicator re-appears across reconnect.
                    if (typeof resumeMessage?.taskId === 'string') setTaskId(resumeMessage.taskId);
                    if (resumeMessage?.errorMessage) {
                        setErrorResult(resumeMessage.errorMessage || 'Something went wrong');
                        return;
                    } else {
                        response = resumeMessage.lastEvent.data;
                    }
                } else if (data?.type === 'PROCESS_STREAM') {
                    response = data.event.data;
                } else if (data?.type === 'PROCESS_ERROR') {
                    setErrorResult(data.errorMessage || 'Something went wrong');
                    setIsExecuting(false);
                    return;
                }

                setIsExecuting(false);
                // A-0008.05: filter `keepalive` events out of the rendered message list
                // (declared in Mind's StreamEvent union but kept invisible to the user).
                const parsed = JSON.parse(response);
                if (parsed?.type === 'keepalive') return;
                setMessages((prev) => [...prev, parsed]);
            } catch (error) {
                console.error('Error parsing message:', error);
                setErrorResult('Something went wrong');
                setIsExecuting(false);
            }
        };

        ws.onerror = (error) => {
            console.error('❌ WebSocket error:', error);
            setErrorResult('WebSocket error');
            setIsExecuting(false);
        };

        ws.onclose = (event) => {
            console.warn('🔌 WebSocket closed:', event.code, event.reason);
            setIsConnected(false);
            setIsExecuting(false);

            if (!isManuallyClosed.current) {
                // Auto-reconnect after 3s
                if (!reconnectTimeout.current) {
                    reconnectTimeout.current = setTimeout(() => {
                        reconnectTimeout.current = null;

                        connect();
                    }, 3000);
                }
            }
        };
    }, [connectionId]);

    useEffect(() => {
        connect();
        return () => {
            isManuallyClosed.current = true;
            socketRef.current?.close();
            if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
        };
    }, [connect]);

    const sendMessage = useCallback((message: object) => {
        setErrorResult('');
        setMessages([]);
        setTaskId(null); // A-0008.05: reset task indicator at the start of a new run

        const ws = socketRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) {
            setIsExecuting(true);
            ws.send(JSON.stringify({
                type: 'START_PROCESS',
                message
            }));
        } else {
            console.warn('⚠️ WebSocket not open. Reconnecting...');
            connect();

            // wait a short delay before retrying send
            setTimeout(() => {
                const currentWs = socketRef.current;
                if (currentWs && currentWs.readyState === WebSocket.OPEN) {
                    setIsExecuting(true);
                    currentWs.send(JSON.stringify({
                        type: 'START_PROCESS',
                        message
                    }));
                } else {
                    console.error('❌ Failed to send message after reconnect attempt.');
                    setErrorResult('Connection unavailable, please try again.');
                }
            }, 1000);
        }
    }, [connect]);

    return {
        socket: socketRef.current,
        isConnected,
        messages,
        errorResult,
        isExecuting,
        sendMessage,
        taskId // A-0008.05
    };
}
